/**
 * PageBuilderContext — Composes all extracted hooks into a single context
 * so sub-components can access builder state via `usePageBuilder()` instead
 * of receiving 25-40 drilled props.
 *
 * Usage (consumer):
 *   const { doc, selection, history, chrome } = usePageBuilder();
 *   doc.sections, doc.updatePageConfig, ...
 *   selection.selectedSection, selection.selectComponent, ...
 *   history.undo(), history.canUndo, ...
 *   chrome.modals.templateLibrary.open(), chrome.zoomLevel, ...
 *
 * Usage (provider — in PageBuilder.js):
 *   <PageBuilderProvider value={{ doc, selection, history, chrome, responsive }}>
 *     <LeftPanel />
 *     ...
 *   </PageBuilderProvider>
 */

import React, { createContext, useContext } from 'react';

const PageBuilderContext = createContext(null);

/**
 * Provider component — wraps the builder UI tree.
 *
 * @param {Object} props.value - The composed builder state object:
 *   { doc, selection, history, chrome, responsive }
 */
export function PageBuilderProvider({ value, children }) {
  return (
    <PageBuilderContext.Provider value={value}>
      {children}
    </PageBuilderContext.Provider>
  );
}

/**
 * Hook to access the PageBuilder context from any sub-component.
 *
 * @returns {{ doc, selection, history, chrome, responsive }}
 * @throws if used outside a PageBuilderProvider
 */
export function usePageBuilder() {
  const ctx = useContext(PageBuilderContext);
  if (!ctx) {
    throw new Error('usePageBuilder must be used within a <PageBuilderProvider>');
  }
  return ctx;
}

export default PageBuilderContext;
